// janus-engine/ability/trigger-consent.ts — OPTIONAL triggers are PROMPT-THEN-RESOLVE (OWNER RULING 2026-10-02, memory
// `feedback_trigger_prompt_5s`; Janus lane A10 — board R-026 dispatch plan, binding for every family):
//   · scope = OPTIONAL triggers only: a REACTION (it spends the reactor's reaction — a reaction_ability carrier, a reaction / opportunity-attack
//     item, a spell cast as a reaction) the player could decline. Mandatory effects (passives, status ticks, regeneration, damage-taken riders)
//     stay automatic;
//   · Ask (the default) prompts the reacting creature's CONTROLLER (its player — a PC's own player, a summon / companion's controller's player) for
//     5 seconds; no answer within 5 s = DECLINE (it does not fire);
//   · an NPC / GM-controlled creature auto-resolves exactly as today (no GM prompt).
//   · an explicit per-reaction Auto preference is standing player consent (owner 2026-11-04); eligibility and resource gates still apply.
// WORKER PICK (owner to review) — how the acting page proceeds: PRE-ASK, BOUNDED WAIT, INLINE. The acting page's extreme-dice dry runs (which
// every served use already runs before its real draw) meet every optional trigger the use can reach; it asks those controllers ONCE, waits at
// most 5 s, then resolves the use with the answers fixed — a yes fires the reaction at the very point PHP fires it today (a pre-roll Evade still
// lands before the attack roll), a no / silence never fires it and spends nothing. A candidate the dry runs did not foresee is unanswered → it
// declines. The question is conditional ("if this lands, use X?"): the reaction may not end up firing at all.
// ENGINE CONTRACT: a reader is supplied ONLY by a page (websocket/trigger-consent.ts); every oracle / census / server-replica reader leaves it
// absent and keeps PHP's auto-fire, so a census judges the legacy behaviour and a live decline is the owner's ruling, never a WRONG.
import { type JanusCtx, type JanusReaders, type Row, ecId, itemField, upper } from './context.js';
import { reactionConsentMode } from './reaction-consent';

export interface TriggerConsentRequest {
  /** `<REACTOR>|<ABILITY>` (upper-case) — one question per reactor and reaction, whatever the frame. */
  key: string;
  reactorId: string;
  abilityId: string;
  /** the reaction's catalog name (display only) */
  abilityName: string;
  /** the triggers_* key that would fire it (display / audit only) */
  trigger: string;
  /** the user who answers (the reactor's controlling player) */
  controllerUserId: string;
}
export interface TriggerConsent {
  /** The controlling PLAYER's user id for a reactor row, or null — an NPC / a GM-controlled creature auto-resolves. '?' = a player may control
   *  it but the page cannot tell who (its controller row is not held): the relay derives the controller from the rows. */
  controller(reactor: Row): string | null;
  /** The controller's answer: true = use it, false = declined (or the 5 s window passed), undefined = not asked yet. */
  answer(reactorId: string, abilityId: string): boolean | undefined;
  /** Record a candidate that needs an answer (met on a dry run, or unforeseen on the real run). */
  need(request: TriggerConsentRequest): void;
  /** The candidates recorded and not answered yet. */
  pending(): TriggerConsentRequest[];
  /** The answers are FINAL: a candidate still unanswered declines (no second round). */
  final: boolean;
}

export const consentKey = (reactorId: string, abilityId: string): string => `${upper(reactorId)}|${upper(abilityId)}`;

/** entry_consumes_reaction() / the reaction bucket: the triggers the ruling makes optional. */
export function isOptionalTrigger(read: Pick<JanusReaders, 'value'>, ability: Row, carrier?: string | null): boolean {
  const type = String(itemField(ability, 'item_type') ?? '');
  return carrier === 'reaction_ability' || type === 'reaction_ability' || type === 'opportunity_attack_ability'
    || String(read.value(ability, 'spell_casting_time') ?? '') === 'reaction';
}

/** Mandatory/NPC triggers retain automatic consent. A player's Auto preference grants consent only;
 * Ask uses the controller's answer, or records a question while dry runs explore the possible fire. */
export function consentAllows(ctx: JanusCtx, reactor: Row, ability: Row, abilityId: string, trigger: string, carrier?: string | null, offer = true): boolean {
  const consent = ctx.read.triggerConsent;
  if (!consent || !isOptionalTrigger(ctx.read, ability, carrier)) return true;
  const controllerUserId = consent.controller(reactor);
  if (!controllerUserId) return true;
  const reactorId = ecId(reactor);
  if (reactionConsentMode(reactor, abilityId) === 'auto') return true;
  const answer = consent.answer(reactorId, abilityId);
  if (answer !== undefined) return answer;
  if (offer) consent.need({ key: consentKey(reactorId, abilityId), reactorId, abilityId: upper(abilityId), abilityName: String(itemField(ability, 'item_name') ?? ''), trigger, controllerUserId });
  // answers FINAL: an unforeseen candidate declines on the dry runs too, so the dry runs prove the very branch the real run takes (fairness)
  return !consent.final && (!offer || !!ctx.dryRun);
}

/** OWNER RULING 2026-10-03 — FORTUNATE (memory `feedback_trigger_prompt_5s`, overrides A10's per-roll pre-ask for the universal Fortunate):
 *  "after a caster gains Fortunate they get prompted if they want to use it if they have their reaction" — NOT a conditional question asked
 *  before every roll that MIGHT land a natural 20 (the extreme-dice dry runs always reach one: A10's census asked 721 such questions). A
 *  luck gain of a creature a PLAYER controls fires nothing inside the use: the gain is recorded as an OFFER (ctx.fortunateOffers → the
 *  verdict's `fortunateOffers`), and the acting page offers Fortunate to that creature's controller AFTER the use commits (5 s card, silence
 *  = decline; the controller's own page spends it as a hand-used Fortunate — websocket/trigger-consent.ts offerFortunate). An NPC / a
 *  GM-controlled creature and a reader-less host (the census) keep PHP's inline auto-fire. Returns true when the gain was deferred. */
export function deferFortunateOffer(ctx: JanusCtx, creature: Row): boolean {
  // WORKER PICK (owner to review): only a KNOWN player controller defers; '?' (the page cannot tell — its controller row or the campaign is not
  // held) keeps A10's pre-ask, where the relay rules it a player's (5 s card) or automatic (an adventure / location encounter: PHP's inline fire)
  if (!isPlayerController(ctx, creature)) return false;
  if (reactionConsentMode(creature, 'AE92D0EF-4E53-4A1D-DD97-B0B2F3732BB8') === 'auto') return false;
  (ctx.fortunateOffers ??= new Set()).add(ecId(creature));
  return true;
}
/** A reactor whose controller the page KNOWS is a player (a user id — never '?', never null). */
export function isPlayerController(ctx: Pick<JanusCtx, 'read'>, reactor: Row): boolean {
  const who = ctx.read.triggerConsent?.controller(reactor);
  return !!who && who !== '?';
}
/** The verdict field carrying a use's deferred Fortunate offers (absent when none). */
export const fortunateOffersOf = (ctx: Pick<JanusCtx, 'fortunateOffers'>): { fortunateOffers?: string[] } =>
  ctx.fortunateOffers?.size ? { fortunateOffers: [...ctx.fortunateOffers] } : {};

/** Before a use's REAL draw: the dry runs met an optional trigger nobody has answered (and the answers are not final) → the page asks first. */
export function consentPending(read: Pick<JanusReaders, 'triggerConsent'>): boolean {
  const consent = read.triggerConsent;
  return !!consent && !consent.final && consent.pending().length > 0;
}
export const TRIGGER_CONSENT_REASON = 'trigger-consent';
